import { useStore } from "lib/store/store";
import SubCard from "components/SubCard";
import { useState, useEffect } from "react";
import { Tool, Subscription } from "lib/types";
import { useSubscriptionApi } from "lib/hooks/useApi";
import { getStoredAuthData, clearAuthData } from "lib/utils";
import { tools } from "lib/data";
import Loader from "components/ui/loader";

type ToolSubscriptionPageProps = {
  toolId: Tool;
  manageToolsEmbedded?: boolean;
};

export default function ToolSubscriptionPage({
  toolId,
  manageToolsEmbedded = false,
}: ToolSubscriptionPageProps) {
  const { setPage, setPopupOpen, setManageToolsActiveTool } = useStore();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [paymentEmail, setPaymentEmail] = useState("");

  const selectedTool: Tool = toolId;
  const toolInfo = tools.find((t) => t.id === selectedTool);
  const { loading, getSubscriptions, updatePhoneNumber } = useSubscriptionApi(selectedTool);

  useEffect(() => {
    const checkAuthAndFetch = async () => {
      const storedAuth = getStoredAuthData(selectedTool);
      if (storedAuth) {
        setPaymentEmail(storedAuth.email);
        const result = await getSubscriptions(storedAuth.email, storedAuth.otp);
        if (result?.success) {
          const list = result.data;
          setSubscriptions(Array.isArray(list) ? list : []);
          setIsAuthenticated(true);
        } else {
          clearAuthData(selectedTool);
          setPopupOpen(selectedTool);
          setIsAuthenticated(false);
        }
      } else {
        setPopupOpen(selectedTool);
        setIsAuthenticated(false);
      }
      setCheckingAuth(false);
    };

    checkAuthAndFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdatePhoneNumber = async (subscriptionId: string, newNumber: string) => {
    if (!paymentEmail) return;

    setUpdatingIds((prev) => new Set(prev).add(subscriptionId));

    try {
      const result = await updatePhoneNumber(subscriptionId, newNumber, paymentEmail);

      if (result?.success) {
        setSubscriptions((prev) =>
          prev.map((sub) =>
            sub.subscriptionId === subscriptionId
              ? {
                  ...sub,
                  phoneNumber: newNumber,
                  userNumber: newNumber,
                  whatsappNumber: newNumber,
                  updateCount: sub.updateCount + 1,
                  remainingUpdates: result.remainingUpdates || sub.remainingUpdates - 1,
                }
              : sub,
          ),
        );
      }
    } finally {
      setUpdatingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(subscriptionId);
        return newSet;
      });
    }
  };

  const handleCancelSuccess = (subscriptionId: string) => {
    setSubscriptions((prev) =>
      prev.map((sub) =>
        sub.subscriptionId === subscriptionId
          ? { ...sub, isCancelled: true, canUndoCancellation: true }
          : sub,
      ),
    );
  };

  const handleKeepPlanSuccess = (subscriptionId: string) => {
    setSubscriptions((prev) =>
      prev.map((sub) =>
        sub.subscriptionId === subscriptionId
          ? { ...sub, isCancelled: false, canUndoCancellation: false }
          : sub,
      ),
    );
  };

  const goBack = () => {
    if (manageToolsEmbedded) {
      setManageToolsActiveTool(null);
      setPage("manage-tools");
    } else {
      setPage("front");
    }
  };

  const handleLogout = () => {
    clearAuthData(selectedTool);
    if (manageToolsEmbedded) {
      setManageToolsActiveTool(null);
      setPage("manage-tools");
    } else {
      setPage("front");
    }
  };

  if (checkingAuth || loading) {
    return (
      <div className="w-full max-w-5xl flex justify-center items-center min-h-[400px]">
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="w-full max-w-5xl">
      <header className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0 mb-[20px] sm:mb-8">
        <div className="text-xl font-bold text-[var(--dash-text-strong)] flex items-center gap-3">
          <img src={toolInfo?.image} alt={toolInfo?.name} className="w-5 h-5" />
          {toolInfo?.name}
        </div>

        <div className="flex gap-3 self-start sm:self-auto">
          <button
            onClick={goBack}
            className="text-sm px-4 py-2 border border-[var(--dash-border)] rounded-md hover:border-[var(--dash-text-strong)] hover:text-[var(--dash-text-strong)] transition-all flex items-center gap-2 cursor-pointer"
          >
            <i className="fas fa-arrow-left"></i>{" "}
            {manageToolsEmbedded ? "All tools" : "Back to Dashboard"}
          </button>
          <button
            onClick={handleLogout}
            className="text-sm px-4 py-2 border border-[#ff4757] text-[#ff4757] rounded-md hover:bg-[#ff4757] hover:text-white transition-all flex items-center gap-2 cursor-pointer"
          >
            <i className="fas fa-sign-out-alt"></i> Logout
          </button>
        </div>
      </header>

      <SubCard
        subscriptions={subscriptions}
        toolId={selectedTool}
        onUpdateWhatsApp={handleUpdatePhoneNumber}
        onCancelSuccess={handleCancelSuccess}
        onKeepPlanSuccess={handleKeepPlanSuccess}
        updatingIds={updatingIds}
      />
    </div>
  );
}

export function TgBulkSenderPage({ manageToolsEmbedded }: { manageToolsEmbedded?: boolean }) {
  return <ToolSubscriptionPage toolId="tgb" manageToolsEmbedded={manageToolsEmbedded} />;
}

export function TgVideoDownloaderPage({ manageToolsEmbedded }: { manageToolsEmbedded?: boolean }) {
  return <ToolSubscriptionPage toolId="tgv" manageToolsEmbedded={manageToolsEmbedded} />;
}

export function TgTranslatorPage({ manageToolsEmbedded }: { manageToolsEmbedded?: boolean }) {
  return <ToolSubscriptionPage toolId="tgt" manageToolsEmbedded={manageToolsEmbedded} />;
}
